import { useEffect, useState } from "react";
import { ClipboardCheck, ThumbsUp, ThumbsDown } from "lucide-react";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { QUALIFICATION_LABELS } from "@/pages/Apply";

/** Lead-admin review queue for counsellor profile-edit requests
 * (specialty/bio/qualification changes) submitted via the "Edit profile"
 * button on a counsellor's own dashboard. Admin-generated ("bypass")
 * accounts skip this queue entirely — their edits apply instantly. */
export default function AdminProfileEditsPanel() {
  const [edits, setEdits] = useState([]);
  const [busyId, setBusyId] = useState(null);

  const load = () => api.get("/admin/profile-edits").then(r => setEdits(r.data || [])).catch(() => {});
  useEffect(() => { load(); }, []);

  const decide = async (id, decision) => {
    setBusyId(id);
    try {
      await api.patch(`/admin/profile-edits/${id}`, { decision });
      toast.success(decision === "approve" ? "Change approved and applied" : "Change rejected");
      load();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusyId(null); }
  };

  const pending = edits.filter(e => e.status === "pending");

  return (
    <div className="card-soft p-8 mb-8" id="profile-edits-section" data-testid="admin-profile-edits-panel">
      <div className="flex items-baseline justify-between mb-4">
        <h2 className="font-serif-display text-2xl inline-flex items-center gap-2">
          <ClipboardCheck className="w-5 h-5" style={{ color: "var(--brand)" }} /> Counsellor profile edits
        </h2>
        <span className="text-xs" style={{ color: "var(--text-secondary)" }}>{pending.length} pending</span>
      </div>
      {pending.length === 0 ? (
        <p style={{ color: "var(--text-secondary)" }}>No profile-edit requests waiting for review.</p>
      ) : (
        <div className="space-y-3" data-testid="profile-edits-list">
          {pending.map(e => (
            <div key={e.id} data-testid={`profile-edit-${e.id}`}
              className="border border-[color:var(--border-soft)] rounded-2xl p-4 flex flex-wrap gap-3 justify-between items-start">
              <div className="min-w-0 flex-1">
                <div className="font-serif-display text-lg">{e.counselor_name}</div>
                <div className="text-xs mb-2" style={{ color: "var(--text-secondary)" }}>{e.counselor_email}</div>
                <div className="text-sm" style={{ color: "var(--brand)" }}>{e.proposed.specialty}</div>
                <div className="text-sm italic" style={{ color: "var(--text-secondary)" }}>"{e.proposed.bio}"</div>
                {e.proposed.qualification_path && (
                  <div className="text-xs mt-1 font-medium" style={{ color: "var(--brand)" }}>
                    Qualification: {QUALIFICATION_LABELS[e.proposed.qualification_path] || e.proposed.qualification_path}
                  </div>
                )}
                {(e.proposed.qualification_certificate_urls || []).map((u, i) => (
                  <a key={u} href={u} target="_blank" rel="noreferrer" className="text-xs mr-3 underline" style={{ color: "var(--brand)" }}>Certificate {i + 1} ↗</a>
                ))}
                {e.proposed.other_qualification_details && (
                  <div className="text-xs mt-1" style={{ color: "var(--text-secondary)" }}>"{e.proposed.other_qualification_details}"</div>
                )}
              </div>
              <div className="flex gap-2">
                <button data-testid={`profile-edit-approve-${e.id}`} disabled={busyId === e.id}
                  onClick={() => decide(e.id, "approve")} className="btn-primary text-sm disabled:opacity-40">
                  <ThumbsUp className="w-4 h-4" /> Approve
                </button>
                <button data-testid={`profile-edit-reject-${e.id}`} disabled={busyId === e.id}
                  onClick={() => decide(e.id, "reject")} className="btn-outline text-sm disabled:opacity-40">
                  <ThumbsDown className="w-4 h-4" /> Reject
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
